import React, { useState, useRef } from 'react';
import { Modal, Box, IconButton, Snackbar, Alert, AppBar, Toolbar } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PreviewPanel from './PreviewPanel';
import './CameraModal.css';
import CameraCapture from '../../components/CameraCapture';
import { Storage } from 'aws-amplify';
import { removeBackground } from '@imgly/background-removal';
import { useSettings } from '../../contexts/settingsContext';
import { useCameraCaptures } from '../../contexts/cameraCapturesContext';
import { useFaceMatching } from '../../hooks/useFaceMatching';

const style = {
  position: 'fixed',
  inset: 0,
  width: '100vw',
  height: '100vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  boxSizing: 'border-box',
  overflow: 'hidden',
};

export default function CameraModal({ open, onClose, username }) {
  // Get camera settings from centralized settings context
  const settings = useSettings();
  const {
    removeBg,
    showBoundingBoxes,
    immediateFaceMatch,
    openSettings,
    autoCreateEventOnAIMatch
  } = settings;

  // Get shared captures from context (includes stealth camera captures)
  const { captures, addCapture, removeCapture } = useCameraCaptures();

  // selected capture index (0 = most recent)
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const [processing, setProcessing] = useState(false);
  const [processingImage, setProcessingImage] = useState(null);
  const fileInputRef = useRef(null);

  // Use face matching hook in interactive mode
  const { matchFace: performFaceMatch } = useFaceMatching({
    silent: false,
    onSuccess: (result, matchedPersons, matchList) => {
      const baseMessage = `Found ${result.matchCount} match(es): ${matchList}`;
      const eventMessage = autoCreateEventOnAIMatch ? ' - MATCH SAVED' : '';
      setSnack({
        open: true,
        severity: 'success',
        message: baseMessage + eventMessage
      });
      setUploading(false);
      // Only close camera if NOT auto-creating events (keep camera open for silent mode behavior)
      if (!autoCreateEventOnAIMatch) {
        onClose();
      }
    },
    onNoMatch: () => {
      setSnack({
        open: true,
        severity: 'info',
        message: 'No exact match found. Searching by detected facial attributes...'
      });
      setUploading(false);
      // Only close camera if NOT auto-creating events
      if (!autoCreateEventOnAIMatch) {
        onClose();
      }
    },
    onError: (err) => {
      setSnack({
        open: true,
        severity: 'error',
        message: 'Face matching failed: ' + (err.message || String(err))
      });
      setUploading(false);
    }
  });

  const handleDeleteCapture = (i) => {
    const captureToDelete = captures[i];
    if (captureToDelete) {
      removeCapture(captureToDelete.id);
    }
    setSelectedIdx(prev => {
      if (prev === null) return null;
      if (prev === i) return 0;
      if (prev > i) return prev - 1;
      return prev;
    });
  };

  async function processCapture(dataUrl) {
    if (!removeBg) return dataUrl;

    setProcessing(true);
    setProcessingImage(dataUrl);
    try {
      const blob = await removeBackground(dataUrl);
      const processedUrl = URL.createObjectURL(blob);
      setProcessing(false);
      setProcessingImage(null);
      return processedUrl;
    } catch (err) {
      console.error('Background removal failed', err);
      setSnack({ open: true, severity: 'warning', message: 'Background removal failed, using original image' });
      setProcessing(false);
      setProcessingImage(null);
      return dataUrl;
    }
  }

  async function matchFace(idxArg) {
    const idx = typeof idxArg === 'number' ? idxArg : selectedIdx ?? 0;
    const capture = captures && captures.length > idx ? captures[idx] : null;
    if (!capture) return;

    try {
      setUploading(true);
      // Use face matching hook (handles compression, network detection, matching, and UI updates)
      await performFaceMatch(capture.id, capture.dataUrl);
    } catch (err) {
      // Error already handled by hook callbacks
    }
  }

  async function uploadCapturedImage(idxArg) {
    const idx = typeof idxArg === 'number' ? idxArg : selectedIdx ?? 0;
    const capture = captures && captures.length > idx ? captures[idx] : null;
    if (!capture) return;
    try {
      setUploading(true);
      const res = await fetch(capture.dataUrl);
      const blob = await res.blob();
      const filename = `profile-${username || 'anonymous'}.png`;
      await Storage.put(filename, blob, { contentType: blob.type });
      setSnack({ open: true, severity: 'success', message: 'Image uploaded' });
      setUploading(false);
      onClose();
    } catch (err) {
      console.error('Upload failed', err);
      setSnack({ open: true, severity: 'error', message: 'Upload failed: ' + (err.message || String(err)) });
      setUploading(false);
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="camera-modal-title"
      >
        <Box sx={style}>
          <AppBar position="static" sx={{ bgcolor: '#2d3e50' }}>
            <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
              <IconButton
                color="inherit"
                aria-label="back"
                onClick={onClose}
                edge="start"
              >
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                color="inherit"
                aria-label="select image from file"
                onClick={() => fileInputRef.current?.click()}
              >
                <PhotoLibraryIcon />
              </IconButton>
              <IconButton
                color="inherit"
                aria-label="settings"
                onClick={openSettings}
              >
                <SettingsIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          {/* main area: camera preview and captured preview - responsive layout, no scrollbars */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'flex-start', justifyContent: captures?.length > 0 ? 'flex-start' : 'center', overflow: 'hidden', px: 1, position: 'relative' }}>
            {/* camera area */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', height: '100%' }}>
              <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <CameraCapture onCapture={(d) => {
                  if (!d) return;
                  // Process in background without blocking
                  processCapture(d).then((processed) => {
                    addCapture(processed, 'camera');
                    setSelectedIdx(0);
                  });
                }}
                uploading={uploading}
                fileInputRef={fileInputRef}
                />
              </Box>
            </Box>
            {/* preview area: sized at ~70% of the camera area (flex: 0.7) */}
            {(captures?.length > 0 || processing) && (
              <PreviewPanel
                captures={captures}
                selectedIdx={selectedIdx}
                onSelect={setSelectedIdx}
                onUpload={uploadCapturedImage}
                onMatch={matchFace}
                onDelete={handleDeleteCapture}
                uploading={uploading}
                processing={processing}
                processingImage={processingImage}
                showBoundingBoxes={showBoundingBoxes}
                immediateFaceMatch={immediateFaceMatch}
                fileInputRef={fileInputRef}
              />
            )}
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ top: '50% !important', transform: 'translateY(-50%)' }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
